'use client'
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, AlertTriangle, Check, LogOut, RefreshCcw, ChevronDown } from 'lucide-react';

const MOBILE_PANEL_MAX_PX = 639;

export default function ConnectedWalletDisplay({
    walletAddress,
    formatAddress,
    connectedWallet,
    solBalance,
    isLoadingSolBalance,
    solBalanceError,
    selectedNetworkData,
    refreshSolBalance,
    disconnectWallet,
}) {
    const [isCopied, setIsCopied] = useState(false);
    const [showMobilePanel, setShowMobilePanel] = useState(false);
    const [panelPosition, setPanelPosition] = useState(null);
    const rootRef = useRef(null);
    const panelRef = useRef(null);

    const handleCopyAddress = (event) => {
        event?.stopPropagation?.();
        navigator.clipboard.writeText(walletAddress);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 1000);
    };

    useEffect(() => {
        if (!showMobilePanel) {
            setPanelPosition(null);
            return undefined;
        }

        const updatePosition = () => {
            const anchor = rootRef.current;
            if (!anchor) return;
            if (window.innerWidth > MOBILE_PANEL_MAX_PX) {
                setShowMobilePanel(false);
                return;
            }
            const rect = anchor.getBoundingClientRect();
            const width = Math.min(288, window.innerWidth - 16);
            const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
            setPanelPosition({
                top: rect.bottom + 6,
                left,
                width,
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [showMobilePanel]);

    useEffect(() => {
        if (!showMobilePanel) return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
            setShowMobilePanel(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setShowMobilePanel(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [showMobilePanel]);

    const symbol = selectedNetworkData?.symbol || 'SOL';
    const balanceAmount = typeof solBalance === 'number' ? solBalance : Number(solBalance) || 0;
    const balanceText = `${balanceAmount.toFixed(6)} ${symbol}`;

    // Keep last known balance visible while refreshing — never flash "Loading..."
    const balanceNode = solBalanceError ? (
        <div className="flex items-center space-x-1 text-red-400">
            <AlertTriangle className="h-3 w-3" />
            <span>Error</span>
        </div>
    ) : (
        balanceText
    );

    const mobilePanel = showMobilePanel && panelPosition && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={panelRef}
                role="dialog"
                aria-label="Connected wallet"
                className="overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-2xl sm:hidden"
                style={{
                    position: 'fixed',
                    top: panelPosition.top,
                    left: panelPosition.left,
                    width: panelPosition.width,
                    zIndex: 180,
                }}
            >
                <div className="border-b border-gray-700/50 px-3 py-3">
                    <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-gray-400">Connected wallet</span>
                        <button
                            type="button"
                            onClick={handleCopyAddress}
                            className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-white"
                        >
                            {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            <span>{isCopied ? 'Copied' : 'Copy'}</span>
                        </button>
                    </div>
                    <div className="break-all font-mono text-sm text-white">
                        {formatAddress(walletAddress)}
                    </div>
                    {connectedWallet ? (
                        <div className="mt-1 text-xs text-gray-400">{connectedWallet}</div>
                    ) : null}
                </div>
                <div className="flex items-center justify-between gap-3 px-3 py-3">
                    <div>
                        <div className="text-xs text-gray-400">Balance</div>
                        <div className="text-sm font-semibold text-white">{balanceNode}</div>
                    </div>
                    <button
                        type="button"
                        onClick={refreshSolBalance}
                        disabled={isLoadingSolBalance}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-600 bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-700 disabled:opacity-50"
                    >
                        <RefreshCcw className={`h-3.5 w-3.5 ${isLoadingSolBalance ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
                <div className="border-t border-gray-700/50 p-2">
                    <button
                        type="button"
                        onClick={() => {
                            setShowMobilePanel(false);
                            disconnectWallet();
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-orange-400 transition-colors hover:bg-orange-600/20"
                    >
                        <LogOut className="h-4 w-4" />
                        Disconnect wallet
                    </button>
                </div>
            </div>,
            document.body,
        )
        : null;

    return (
        <div className="relative dropdown-container" ref={rootRef}>
            <button
                type="button"
                onClick={() => setShowMobilePanel((open) => !open)}
                className="flex min-w-0 max-w-full items-center gap-1 rounded-lg border border-gray-600/50 bg-gray-800/60 px-1.5 py-1 text-white transition-colors hover:border-gray-500 hover:bg-gray-800 max-[349px]:hidden sm:hidden"
                aria-expanded={showMobilePanel}
                aria-haspopup="dialog"
                aria-label={`Wallet ${formatAddress(walletAddress)}. Open balance and disconnect`}
            >
                <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-green-400" />
                <span className="min-w-0 truncate text-xs font-medium">{formatAddress(walletAddress)}</span>
                <ChevronDown className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${showMobilePanel ? 'rotate-180' : ''}`} />
            </button>

            <div className="hidden min-w-0 max-w-full items-center gap-2 rounded-lg border border-gray-600/50 bg-gray-800/60 px-3 py-2 text-white sm:flex">
                <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-green-400" />
                <span className="min-w-0 truncate text-sm font-medium">{formatAddress(walletAddress)}</span>
                <div className="text-right">
                    <div className="text-xs font-bold text-gray-200">
                        {balanceNode}
                    </div>
                    <div className="text-xs text-gray-400">{connectedWallet}</div>
                </div>
                <div className="flex shrink-0 items-center space-x-1">
                    <button
                        type="button"
                        onClick={refreshSolBalance}
                        disabled={isLoadingSolBalance}
                        className="p-1 text-gray-400 transition-colors hover:text-gray-200 disabled:opacity-50"
                        title="Refresh balance"
                    >
                        <RefreshCcw className={`h-3 w-3 ${isLoadingSolBalance ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        type="button"
                        onClick={handleCopyAddress}
                        className="p-1 text-gray-400 transition-colors hover:text-gray-200"
                        title="Copy address"
                    >
                        {isCopied ? (
                            <Check className="h-3 w-3" />
                        ) : (
                            <Copy className="h-3 w-3" />
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={disconnectWallet}
                        className="p-1 text-gray-400 transition-colors hover:text-red-400"
                        title="Disconnect wallet"
                    >
                        <LogOut className="h-3 w-3" />
                    </button>
                </div>
            </div>
            {mobilePanel}
        </div>
    );
}
